import { FunctionComponent, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLinkify } from '../../hooks';
import { escapeHtml, isKeyboardFocus } from '../../utils';

import classNames from 'classnames';
import classes from './LineClamp.module.scss';

export interface LineClampProps {
  maxLines: number;
  stringContent?: string;
  stringContentIcon?: JSX.Element;
  htmlStringContent?: string;
  contentClassName?: string;
  allowExpand?: boolean;
  expandCharsLimit?: number;
  expandMaxHeight?: number;
  readMore?: string | JSX.Element;
  readMoreClassName?: string;
  nonClickable?: boolean;
  onClick?: () => void;
  onTextReveal?: () => void;
  doLinkify?: boolean;
  fallBackLineHeight?: number;
}

export const LineClamp: FunctionComponent<LineClampProps> = memo(
  ({
    maxLines,
    stringContent,
    stringContentIcon,
    htmlStringContent,
    contentClassName,
    readMore,
    readMoreClassName,
    nonClickable,
    onClick,
    onTextReveal,
    allowExpand,
    expandCharsLimit = 650,
    expandMaxHeight = 500,
    doLinkify,
    fallBackLineHeight = 24,
  }) => {
    const { t } = useTranslation();

    const { linkify } = useLinkify();

    const [maxHeight, setMaxHeight] = useState<string>('unset');
    const [isOverflown, setIsOverflown] = useState(false);
    const [contentRef, setContentRef] = useState<HTMLDivElement | null>(null);

    const html = useMemo(() => {
      if (!htmlStringContent) {
        return '';
      }

      if (!doLinkify) {
        return htmlStringContent;
      }

      return linkify(htmlStringContent);
    }, [doLinkify, htmlStringContent, linkify]);

    const stringContentNode = useMemo(() => {
      if (!stringContent) {
        return null;
      }

      if (!doLinkify) {
        return stringContent;
      }

      return <span dangerouslySetInnerHTML={{ __html: linkify(escapeHtml(stringContent)) }}></span>;
    }, [doLinkify, linkify, stringContent]);

    const refCallback = useCallback(
      (node: HTMLDivElement | null) => {
        if (node !== null) {
          setMaxHeight(
            `${
              maxLines * (parseInt(window.getComputedStyle(node).lineHeight) || fallBackLineHeight)
            }px`
          );
          setContentRef(node);
        }
      },
      [fallBackLineHeight, maxLines]
    );

    useEffect(() => {
      if (maxHeight && contentRef) {
        const { scrollHeight, clientHeight } = contentRef;

        //needed in case of bold text that takes a little bit more space than regular
        const delta = 0.5 * parseInt(window.getComputedStyle(contentRef).lineHeight);

        setIsOverflown(scrollHeight - delta > clientHeight);
      }
      //stringContent, htmlStringContent dependencies added for 'edit card' case
    }, [contentRef, maxHeight, stringContent, htmlStringContent]);

    const onClickHandler = useCallback(
      ({ target }: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (
          allowExpand &&
          contentRef &&
          contentRef.innerText.length <= expandCharsLimit &&
          contentRef.scrollHeight <= expandMaxHeight
        ) {
          if (isKeyboardFocus()) {
            contentRef.focus({ preventScroll: true });
          }

          setMaxHeight('unset');
          setIsOverflown(false);

          onTextReveal?.();
        } else {
          if ((target as HTMLElement).tagName.toLowerCase() === 'a') {
            return;
          }
          onClick?.();
        }
      },
      [allowExpand, contentRef, expandCharsLimit, expandMaxHeight, onClick, onTextReveal]
    );

    const isReadMoreClickable = !nonClickable && Boolean(allowExpand || onClick);

    const ReadMoreTag = isReadMoreClickable ? 'button' : 'div';

    return (
      <div
        className={classNames(classes['line-clamp'], {
          [classes['line-clamp--clickable']]: isOverflown && !nonClickable,
        })}
        onClick={(event) => isOverflown && onClickHandler(event)}
      >
        <div className={classes['line-clamp__content']}>
          {htmlStringContent && (
            <div
              ref={refCallback}
              style={{ maxHeight }}
              className={classNames(contentClassName)}
              dangerouslySetInnerHTML={{ __html: html }}
              {...(allowExpand && { tabIndex: -1 })}
            ></div>
          )}
          {stringContent && (
            <div
              ref={refCallback}
              style={{ maxHeight }}
              className={classNames(contentClassName)}
              {...(allowExpand && { tabIndex: -1 })}
            >
              {stringContentNode} {stringContentIcon}
            </div>
          )}
        </div>
        {isOverflown && (
          <ReadMoreTag
            className={classNames(classes['line-clamp__read-more'], readMoreClassName, {
              [classes['line-clamp__read-more--non-clickable']]: nonClickable,
            })}
            {...(isReadMoreClickable && { type: 'button', 'aria-label': t('common.read-more') })}
          >
            {readMore}
          </ReadMoreTag>
        )}
      </div>
    );
  }
);
